<template>
  <div>
    <LoadingContainer v-if="loading"/>

    <div v-else class="container">
      <div class="row">
        <div class="col-xs-6">
          <help-text>
            <h3>
              Edit Ad
            </h3>

            <p>
              Edit the details of the ad.
            </p>
          </help-text>
        </div>

        <div class="col-xs-18">
          <div class="panel panel-default">
            <div class="panel-body">
              <form @submit.prevent="submit" class="form-horizontal">
                <SelectFormGroup
                    v-model="data.publication_based_ad_format_id"
                    name="publication_based_ad_format_id"
                    label="Ad Format"
                    help-text="The ad format."
                    :error="getError('publication_based_ad_format_id')"
                    :options="publicationBasedAdFormatOptions"
                    :disabled="publicationAd.attributes.is_locked && missingPermission('admin')"
                />

                <SelectFormGroup
                    v-model="data.publication_based_ad_location_id"
                    name="publication_based_ad_location_id"
                    label="Ad Location"
                    help-text="The ad location."
                    :error="getError('publication_based_ad_location_id')"
                    :options="publicationBasedAdLocationOptions"
                    :disabled="publicationAd.attributes.is_locked && missingPermission('admin')"
                    :nullable="true"
                />

                <SelectFormGroup
                    v-model="data.publication_id"
                    name="publication_id"
                    label="Publication"
                    help-text="The publication."
                    :error="getError('publication_id')"
                    :options="publicationOptions"
                    :disabled="publicationAd.attributes.is_locked && missingPermission('admin')"
                />

                <SelectFormGroup
                    v-model="data.project_id"
                    name="project_id"
                    label="Project"
                    help-text="The project that the ad belongs to."
                    :options="projectOptions"
                    :nullable="true"
                />

                <NumberFormGroup
                    v-model="data.price"
                    name="price"
                    label="Price"
                    help-text="The price of the ad."
                    :error="getError('price')"
                    :disabled="publicationAd.attributes.is_locked && missingPermission('admin')"
                />

                <NumberFormGroup
                    v-model="data.agency_commission"
                    name="agency_commission"
                    label="Agency Commission"
                    help-text="The agency commission of the ad."
                    :error="getError('agency_commission')"
                    :disabled="publicationAd.attributes.is_locked && missingPermission('admin')"
                />

                <NumberFormGroup
                    v-model="data.third_party_costs"
                    name="third_party_costs"
                    label="Third Party Costs"
                    help-text="The third party costs of the ad."
                    :error="getError('third_party_costs')"
                    :disabled="publicationAd.attributes.is_locked && missingPermission('admin')"
                />

                <TextFormGroup
                    v-model="data.url"
                    name="url"
                    label="URL"
                    help-text="The URL that the ad should point at (optional)."
                    :error="getError('url')"
                />

                <TextFormGroup
                    v-model="data.content"
                    name="content"
                    label="Material Number"
                    help-text="The material number for the ad (optional)."
                    :error="getError('content')"
                />

                <TextFormGroup
                    v-model="data.dtp_comment"
                    name="dtp_comment"
                    label="DTP Comment"
                    help-text="The comment for the DTP (optional)."
                    :error="getError('dtp_comment')"
                />

                <TextFormGroup
                    v-model="data.customer_comment"
                    name="customer_comment"
                    label="Customer Comment"
                    help-text="The comment for the customer (optional)."
                    :error="getError('customer_comment')"
                />

                <hr>

                <div class="form-group">
                  <div class="col-sm-offset-8 col-sm-12">
                    <button type="submit" class="btn btn-black">
                      <i class="fa fa-save"></i> Save
                    </button>

                    <button
                        type="button"
                        class="btn btn-white pull-right"
                        style="border: 1px solid #000000;"
                        data-toggle="modal"
                        data-target="#find-ad-modal"
                    >
                      <i class="fa fa-search"></i> Find DTP Info
                    </button>

                    <div class="clearfix"></div>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      <FindAdModal :ads="ads" @select="selectAd"/>
    </div>
  </div>
</template>

<script>
import http from '@/http'
import moment from 'moment'
import notify from '@/notify'
import Errors from '@/mixins/Errors'
import FindAdModal from '@/pages/Companies/Companies/Edit/Modals/FindAd'
import HelpText from '@/components/HelpText'
import LoadingContainer from '@/components/Layout/LoadingContainer'
import NumberFormGroup from '@/vendor/forms/FormGroups/Horizontal/Number'
import Permissions from '@/mixins/Permissions'
import SelectFormGroup from '@/vendor/forms/FormGroups/Horizontal/Select'
import StatusLabel from '@/components/StatusLabel'
import SubmitFormGroup from '@/vendor/forms/FormGroups/Horizontal/Submit'
import TextFormGroup from '@/vendor/forms/FormGroups/Horizontal/Text'

export default {
  components: {
    FindAdModal,
    HelpText,
    LoadingContainer,
    NumberFormGroup,
    SelectFormGroup,
    StatusLabel,
    SubmitFormGroup,
    TextFormGroup
  },

  mixins: [Errors, Permissions],

  model: {
    prop: 'company',
    event: 'company-updated'
  },

  props: ['company', 'orderId', 'publicationBasedAdId'],

  data() {
    return {
      publicationAd: null,
      data: {},

      ads: null,
      publications: null,
      publicationBasedAdFormats: null,
      publicationBasedAdLocations: null,
      projects: null,

      loading: true
    }
  },

  mounted() {
    http.get('/api/v2/publication-based-ads/' + this.publicationBasedAdId)
        .then(response => {
          this.publicationAd = response.data.data

          this.data = response.data.data.attributes

          Promise.all([
            http.get('/api/v1/ads', {
              company: this.company.data.id,
              starts_from: moment().subtract(1, 'year').format('YYYY-MM-DD')
            }),
            http.getList('/api/v2/publications', {
              'filter[publication_channel_id]': this.publicationAd.attributes.publication_based_channel_id,
              'filter[publish_from]': moment().format('YYYY-MM-DD'),
            }),
            http.getList('/api/v2/publication-based-ad-formats', {
              'filter[publication_based_channel_id]': this.publicationAd.attributes.publication_based_channel_id,
            }),
            http.getList('/api/v2/publication-based-ad-locations', {
              'filter[publication_based_channel_id]': this.publicationAd.attributes.publication_based_channel_id,
            }),
            http.getList('/api/v2/projects', {'filter[status]': 'active'}),
          ])
              .then(responses => {
                this.ads = responses[0].data

                this.publications = responses[1]

                this.publicationBasedAdFormats = responses[2]

                this.publicationBasedAdLocations = responses[3]

                this.projects = responses[4]

                this.loading = false
              })
        })
  },

  computed: {
    publicationOptions() {
      let options = this.publications.map(publication => ({value: publication.id, label: publication.attributes.name}))

      if (!options.map(option => option.value).includes(this.publicationAd.attributes.publication_id)) {
        options.push({value: this.publicationAd.attributes.publication_id, label: this.publicationAd.attributes.publication_name, disabled: true})
      }

      return options
    },

    publicationBasedAdFormatOptions() {
      return this.publicationBasedAdFormats.map(publicationBasedAdFormat => {
        return {
          value: publicationBasedAdFormat.id,
          label: publicationBasedAdFormat.attributes.name
        }
      })
    },

    publicationBasedAdLocationOptions() {
      return this.publicationBasedAdLocations.map(publicationBasedAdLocation => {
        return {
          value: publicationBasedAdLocation.id,
          label: publicationBasedAdLocation.attributes.name
        }
      })
    },

    projectOptions() {
      return this.projects.map(project => ({value: project.id, label: project.attributes.portal_name + ' - ' +project.attributes.project_category_name + ' - ' + project.attributes.name}))
    }
  },

  methods: {
    submit() {
      http.put('/api/v2/publication-based-ads/' + this.publicationAd.id, this.data)
          .then(() => {
            notify.success('The ad was saved')

            http.get('/api/v1/companies/' + this.company.data.id)
                .then(response => {
                  this.$emit('company-updated', response.data)

                  this.$router.push('..')
                })
          })
          .catch(this.catchValidationErrors)
    },

    selectAd(ad) {
      this.data = {
        ...this.data,
        url: ad.url,
        content: ad.content
      }
    }
  }
}
</script>
